<script setup lang="ts">
/**
 * The record of what was changed, and by whom (SPEC §6.32, §8.1 — MT-110).
 *
 * Read as sentences: "Sam reset a household's link — The Daces". The server
 * names every actor and target and describes every action, so nothing here
 * turns a code or an id into words, and nothing here could render one by
 * mistake. The stored metadata is not in the response at all; that is the
 * protection for SPEC's "no secrets, tokens, or full household URLs", and it
 * lives server-side where a template change cannot undo it.
 *
 * Times are shown in the group's zone, as every other time in the organizer's
 * screens is. Two organizers in different places reading "Tuesday 9:14 PM"
 * should mean the same moment.
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { endpoints, type AuditPageResponse } from '@/api'
import { useGroupContextStore } from '@/stores/groupContext'
import { nextDay, startOfDayUtc } from '@/utils/dates'
import { asCount } from '@/utils/numbers'
import EmptyState from '@/components/EmptyState.vue'

const PageSize = 25

const route = useRoute()
const groupContext = useGroupContextStore()
const groupId = route.params.groupId as string

const result = ref<AuditPageResponse | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)

const page = ref(1)
const action = ref<string | null>(null)
const from = ref('')
const until = ref('')

/**
 * The group's zone, or the reader's while the group has not loaded.
 *
 * Found by this page's own route param rather than the layout's selection, so
 * the page means the same thing however it was reached.
 */
const timeZone = computed(
  () =>
    groupContext.groups.find((g) => g.id === groupId)?.timeZoneId ??
    Intl.DateTimeFormat().resolvedOptions().timeZone,
)

const actionOptions = computed(() => [
  { title: 'Everything', value: null },
  ...(result.value?.actions ?? []).map((a) => ({
    title: sentenceCase(a.description),
    value: a.action,
  })),
])

const pages = computed(() => {
  if (!result.value) return 1
  const size = asCount(result.value.pageSize, PageSize)
  return Math.max(1, Math.ceil(asCount(result.value.total) / size))
})

const rangeBackwards = computed(
  () => from.value !== '' && until.value !== '' && from.value > until.value,
)

const filtered = computed(() => action.value !== null || from.value !== '' || until.value !== '')

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function when(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timeZone.value,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

async function load(): Promise<void> {
  if (rangeBackwards.value) return

  loading.value = true
  error.value = null

  try {
    result.value = await endpoints.listGroupAudit(groupId, {
      page: page.value,
      pageSize: PageSize,
      action: action.value ?? undefined,
      fromUtc: from.value ? (startOfDayUtc(from.value, timeZone.value) ?? undefined) : undefined,
      // Inclusive to the person picking it: "until the 30th" includes the 30th.
      toUtc: until.value
        ? (startOfDayUtc(nextDay(until.value), timeZone.value) ?? undefined)
        : undefined,
    })
  } catch {
    error.value = 'The record could not be loaded just now. Try again in a moment.'
  } finally {
    loading.value = false
  }
}

function clearFilters(): void {
  action.value = null
  from.value = ''
  until.value = ''
}

// A new filter starts from the first page; a new page keeps the filter.
watch([action, from, until], () => {
  if (page.value !== 1) page.value = 1
  else void load()
})

watch(page, () => void load())

onMounted(async () => {
  await groupContext.ensureLoaded()
  await load()
})
</script>

<template>
  <v-container class="audit" style="max-width: 56rem">
    <h1 class="text-h5 mb-1">Activity</h1>
    <p class="audit__intro">Every change made to this group, newest first.</p>

    <div class="audit__filters">
      <v-select
        v-model="action"
        :items="actionOptions"
        label="What happened"
        density="comfortable"
        hide-details
        data-testid="audit-action"
      />
      <v-text-field
        v-model="from"
        type="date"
        label="From"
        density="comfortable"
        hide-details="auto"
        name="from"
      />
      <v-text-field
        v-model="until"
        type="date"
        label="Until"
        density="comfortable"
        :error-messages="rangeBackwards ? ['This is before the start date.'] : []"
        hide-details="auto"
        name="until"
      />
    </div>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

    <template v-if="result && !error">
      <EmptyState
        v-if="result.entries.length === 0"
        icon="mdi-history"
        :headline="filtered ? 'Nothing matches' : 'Nothing recorded yet'"
        :description="
          filtered
            ? 'Nothing happened that fits those choices. Try a wider range or a different action.'
            : 'Changes to households, meals and settings will be listed here as they happen.'
        "
      >
        <template #action>
          <v-btn v-if="filtered" variant="text" @click="clearFilters">Show everything</v-btn>
        </template>
      </EmptyState>

      <v-list v-else lines="two" class="audit__list" data-testid="audit-entries">
        <v-list-item v-for="entry in result.entries" :key="entry.id" data-testid="audit-entry">
          <v-list-item-title class="audit__line">
            <strong>{{ entry.actorName }}</strong> {{ entry.description }}
            <template v-if="entry.targetName">
              — <span data-testid="audit-target">{{ entry.targetName }}</span>
            </template>
          </v-list-item-title>
          <v-list-item-subtitle>
            <time :datetime="entry.occurredUtc">{{ when(entry.occurredUtc) }}</time>
          </v-list-item-subtitle>
        </v-list-item>
      </v-list>

      <v-pagination
        v-if="pages > 1"
        v-model="page"
        :length="pages"
        :total-visible="5"
        class="mt-4"
        aria-label="Pages of activity"
      />
    </template>
  </v-container>
</template>

<style scoped>
.audit {
  padding-top: 24px;
}

.audit__intro {
  margin: 0 0 16px;
  color: rgb(var(--v-theme-on-surface-variant));
}

.audit__filters {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

@media (max-width: 600px) {
  .audit__filters {
    grid-template-columns: 1fr 1fr;
  }

  .audit__filters > :first-child {
    grid-column: 1 / -1;
  }
}

/* Sentences, not labels: let them wrap rather than truncate. */
.audit__line {
  white-space: normal;
  line-height: 1.4;
}
</style>
